import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Layout from '../../component/layout';


// --- Bootstrap CSS Injection ---
const useBootstrapStyles = () => {
    // useEffect(() => {
    //     if (!document.getElementById('bootstrap-css')) {
    //         const link = document.createElement('link');
    //         link.id = 'bootstrap-css';
    //         link.rel = 'stylesheet';
    //         link.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css';
    //         document.head.appendChild(link);
    //     }
    // }, []);
};


// --- Mock Data ---
const INITIAL_DATA = [
    {
        id: "about_us",
        name: "About Us",
        subModules: [
            {
                id: "view_about",
                name: "General Settings",
                permissions: [{ id: "perm_view_about", name: "View About Us" }]
            }
        ]
    },
    {
        id: "acct_rec",
        name: "Account Receivable",
        subModules: [
            {
                id: "adv_receipt",
                name: "Advance Receipt",
                permissions: [
                    { id: "v_adv", name: "View Advance Receipt" },
                    { id: "c_adv", name: "Create Advance Receipt" },
                    { id: "e_adv", name: "Edit Advance Receipt" },
                    { id: "d_adv", name: "Delete Advance Receipt" }
                ]
            },
            {
                id: "money_receipt",
                name: "Money Receipt",
                permissions: [
                    { id: "v_mon", name: "View Money Receipt" },
                    { id: "p_mon", name: "Print Money Receipt" }
                ]
            },
            {
                id: "gen_inv",
                name: "Generate Invoice",
                permissions: [
                    { id: "exp_inv", name: "Export Invoice" },
                    { id: "v_gen_inv", name: "View Generate Invoice" }
                ]
            }
        ]
    },
    {
        id: "manage_ledgers",
        name: "Manage Ledgers",
        subModules: [
            {
                id: "led_ops",
                name: "Ledger Operations",
                permissions: [
                    { id: "c_led_grp", name: "Create Ledger Group" },
                    { id: "c_led_mas", name: "Create Ledger Master" },
                    { id: "c_pri_grp", name: "Create Primary Ledger Group" },
                    { id: "c_trans", name: "Create Transaction" },
                    { id: "d_led_grp", name: "Delete Ledger Group" },
                    { id: "d_led_mas", name: "Delete Ledger Master" },
                    { id: "v_led_grp", name: "View Ledger Group" },
                    { id: "v_led_mas", name: "View Ledger Master" }
                ]
            }
        ]
    },
    {
        id: "inventory",
        name: "Inventory Management",
        subModules: [
            {
                id: "stock_items",
                name: "Stock Items",
                permissions: [
                    { id: "v_stock", name: "View Stock" },
                    { id: "a_stock", name: "Add Stock" }
                ]
            }
        ]
    }
];


// --- Level 3: Individual Permission ---
const PermissionItem = React.memo(({ permission, isChecked, onChange }) => (
    <div className="col-xl-3 col-lg-4 col-md-6 mb-2">
        <div
            className={`form-check p-2 border rounded bg-white hover-shadow transition-all d-flex align-items-center ${isChecked ? 'border-primary' : 'border-light-subtle'}`}
            style={{ minHeight: '45px', borderStyle: 'solid', borderWidth: '1px', cursor: 'pointer' }}
            onClick={() => onChange(permission?.PermissionId, !permission.IsSelected)}
        >
            <input
                className="form-check-input mt-0 ms-1 me-2"
                type="checkbox"
                checked={permission.IsSelected}
                onChange={(e) => { onChange(permission?.PermissionId, e.target.checked) }} // React controlled component requirement
                style={{
                    pointerEvents: 'none', // Critical: Let the click bubble to the parent div
                    width: '1.2em',
                    height: '1.2em',
                    flexShrink: 0
                }}
            />
            <label
                className="form-check-label small text-dark fw-medium mb-0 flex-grow-1 text-truncate"
                style={{
                    pointerEvents: 'none', // Critical: Let the click bubble to the parent div
                    userSelect: 'none'
                }}
            >
                {permission.PermissionName}
            </label>
        </div>
    </div>
));


// --- Level 2: Sub-Module Group ---
const SubModuleSection = ({ subModule, selectedIds, onBatchChange, onSingleChange, areAllSelected }) => {
    const subIds = subModule.SubPermissions.map(p => p.id);
    const selectedCount = subIds.filter(id => selectedIds.has(id)).length;

    const isAllSelected = selectedCount === subIds.length && subIds.length > 0;
    const isIndeterminate = selectedCount > 0 && selectedCount < subIds.length;

    const checkboxRef = useRef();


    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);


    return (
        <div className="mb-4 bg-white border rounded shadow-sm overflow-visible">
            <div className="bg-light px-3 py-2 d-flex align-items-center justify-content-between border-bottom">
                <h6 className="mb-0 fw-bold text-dark text-truncate me-2" style={{ fontSize: '0.85rem' }}>
                    {subModule.PermissionName}
                </h6>

                <div className="d-flex align-items-center flex-nowrap ms-auto">
                    <label
                        className="x-small text-muted me-2 fw-semibold mb-0"
                        style={{ whiteSpace: 'nowrap', userSelect: 'none' }}
                    >
                        {isAllSelected ? 'All Selected' : isIndeterminate ? 'Partial' : 'Select All'}
                    </label>
                    <div className="form-check form-switch mb-0 p-0 d-flex">
                        <input
                            className="form-check-input m-0 indeterminate-switch"
                            type="checkbox"
                            checked={areAllSelected}
                            ref={checkboxRef}
                            onChange={(e) => {
                                e.stopPropagation();
                                onBatchChange(subModule.PermissionId, e.target.checked);
                            }}
                            style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                        />
                    </div>
                </div>
            </div>
            <div className="p-3">
                <div className="row g-2">
                    {subModule.SubPermissions.map(perm => (
                        <PermissionItem
                            key={perm.id}
                            permission={perm}
                            isChecked={selectedIds.has(perm.id)}
                            onChange={(id, isSelected) => onSingleChange(id, isSelected)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};


// --- Search / Filter Component ---
const ModuleSearchBox = ({ value, onChange }) => {
    return (
        <div className="search-wrapper">
            <div className="search-input-group">
                <span className="search-icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                    >
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                    </svg>
                </span>

                <input
                    type="text"
                    className="search-input"
                    placeholder="Search Module, Sub-Module or Permission..."
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />

                {value && (
                    <button
                        className="clear-btn"
                        type="button"
                        onClick={() => onChange("")}
                    >
                        &times;
                    </button>
                )}
            </div>
        </div>
    );
};


// --- Main App Component ---
export default function AccessPermission({ permissionData, setPermissions,
    handleGetPermissionByRoleID,
    roles,
    updatePermissions,
    setPermissionData,
    selectedPermission
}) {
    useBootstrapStyles();
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [role, setRole] = useState("");
    const [openModuleId, setOpenModuleId] = useState(INITIAL_DATA[0].id);
    const [searchQuery, setSearchQuery] = useState("");
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const moduleRefs = useRef({});


    // Deep filtering logic: Module -> Sub-Module -> Permissions
    // Combined with Show Selected Only logic
    const filteredData = useMemo(() => {
        if (!permissionData || permissionData.length === 0) return [];

        const query = searchQuery?.toLowerCase().trim() || "";

        return permissionData
            .map((category) => {

                const moduleMatch =
                    query !== "" &&
                    category.CategoryName?.toLowerCase().includes(query);

                const filteredPermissions = (category.Permissions || [])
                    .map((perm) => {

                        const subModuleMatch =
                            query !== "" &&
                            perm.PermissionName?.toLowerCase().includes(query);

                        const subPerms = Array.isArray(perm.SubPermissions)
                            ? perm.SubPermissions
                            : [];

                        let subPermissions =
                            subPerms.length > 0
                                ? subPerms
                                : [perm];

                        // 🔍 SEARCH FILTER
                        if (query !== "") {
                            const searchFiltered = subPermissions.filter((p) =>
                                p.PermissionName?.toLowerCase().includes(query)
                            );

                            subPermissions =
                                moduleMatch || subModuleMatch
                                    ? subPermissions
                                    : searchFiltered;
                        }

                        // ✅ SHOW SELECTED ONLY
                        if (showSelectedOnly) {
                            subPermissions = subPermissions.filter((p) =>
                                p?.IsSelected
                            );
                        }

                        if (subPermissions.length > 0) {
                            return {
                                ...perm,
                                SubPermissions: subPermissions
                            };
                        }

                        return null;
                    })
                    .filter(Boolean);

                if (filteredPermissions.length > 0) {
                    return {
                        ...category,
                        Permissions: filteredPermissions
                    };
                }

                return null;
            })
            .filter(Boolean);

    }, [
        permissionData,
        searchQuery,
        showSelectedOnly,
        selectedPermissions
    ]);



    useEffect(() => {

        getSelectedSubPermissionIds(permissionData);

    }, [permissionData])


    const getSelectedSubPermissionIds = (data) => {
        const ids = [];

        data.forEach(category => {
            category.Permissions.forEach(permission => {

                // If it has sub-permissions → collect from children
                if (Array.isArray(permission.SubPermissions) && permission.SubPermissions.length > 0) {
                    permission.SubPermissions.forEach(sub => {
                        if (sub.IsSelected) {
                            ids.push(sub.PermissionId);
                        }
                    });
                }
                // If no sub-permissions → collect parent itself
                else {
                    if (permission.IsSelected) {
                        ids.push(permission.PermissionId);
                    }
                }

            });
        });

        setPermissionData(prev => ({
            ...prev,
            permissionsList: ids
        }))

        console.log(ids);
    };


    const handleSingleToggle = useCallback((id) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);


    const handleBatchToggle = useCallback((ids, shouldSelect) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            ids.forEach(id => {
                if (shouldSelect) next.add(id);
                else next.delete(id);
            });
            return next;
        });
    }, []);


    const toggleAccordion = (id) => {
        setOpenModuleId(openModuleId === id ? null : id);
    };



    const handleSelectSinglePermissions = () => {



    }

    const handleUpdateAllPer = (Category, isSelected) => {

        setPermissions(prev =>
            prev.map((cat, cIndex) => {
                if (cat?.CategoryName !== Category) return cat;

                return {
                    ...cat,
                    Permissions: cat.Permissions.map(perm => ({
                        ...perm,
                        IsSelected: isSelected,
                        SubPermissions: Array.isArray(perm.SubPermissions)
                            ? perm.SubPermissions.map(sub => ({
                                ...sub,
                                IsSelected: isSelected
                            }))
                            : perm.SubPermissions
                    }))
                };
            })
        );

    }

    const updateModuleSubmodule = (permissionId, isSelected) => {

        console.log(permissionId, isSelected);


        setPermissions(prev =>
            prev.map(cat => ({
                ...cat,
                Permissions: cat.Permissions.map(perm => {

                    if (perm.PermissionId !== permissionId) return perm;

                    return {
                        ...perm,
                        IsSelected: isSelected,
                        SubPermissions: Array.isArray(perm.SubPermissions)
                            ? perm.SubPermissions.map(sub => ({
                                ...sub,
                                IsSelected: isSelected
                            }))
                            : perm.SubPermissions
                    };
                })
            }))
        );

    }

    const handleSinglePermissionChange = (permissionId, isSelected) => {
        console.log(permissionId, isSelected);

        setPermissions(prev =>
            prev.map(category => ({
                ...category,
                Permissions: category.Permissions.map(permission => {

                    // ✅ Case 1: No sub-permissions → update parent directly
                    if (!permission.SubPermissions || permission.SubPermissions.length === 0) {
                        return permission.PermissionId === permissionId
                            ? { ...permission, IsSelected: isSelected }
                            : permission;
                    }

                    // ✅ Case 2: Has sub-permissions → update only child
                    const updatedSubPermissions = permission.SubPermissions.map(sub =>
                        sub.PermissionId === permissionId
                            ? { ...sub, IsSelected: isSelected }
                            : sub
                    );

                    return {
                        ...permission,
                        SubPermissions: updatedSubPermissions
                    };
                })
            }))
        );
    };
    const areAllSelected = (permissions = []) => {
        if (!permissions.length) return false;

        return permissions.every(permission => {

            // If it has children → check children
            if (Array.isArray(permission.SubPermissions) && permission.SubPermissions.length > 0) {
                return areAllSelected(permission.SubPermissions);
            }

            // Leaf node
            return permission.IsSelected === true;
        });
    };


    return (

        <div className="bg-light min-vh-100 py-4" style={{ color: '#212529' }}>
            <div className="container" >

                <div className="d-flex flex-wrap justify-content-between align-items-end mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Role Permissions</h4>
                        <p className="text-muted small mb-0">Search and configure access for your roles</p>
                    </div>
                    <ModuleSearchBox value={searchQuery} onChange={setSearchQuery} />
                </div>


                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4 bg-white rounded">
                        <label className="form-label text-secondary small fw-bold mb-2">ASSIGN TO ROLE</label>
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <select
                                className="form-select border-secondary-subtle shadow-none"
                                value={role}
                                onChange={(e) => {


                                    let indexOf = roles.findIndex(r => r.value == e.target.value);
                                    setRole(roles[indexOf]?.value);
                                    handleGetPermissionByRoleID(roles[indexOf]);

                                }}
                                style={{ maxWidth: '350px', height: '45px' }}
                            >
                                <option value="">Select a role...</option>
                                {
                                    roles?.map(r => <option key={r.value} value={r.value}>{r.label}</option>)
                                }

                            </select>


                            <button
                                className={`btn btn-lg d-flex align-items-center gap-2 fw-bold transition-all ${showSelectedOnly ? 'btn-primary' : 'btn-outline-primary'}`}
                                style={{ height: '45px', fontSize: '0.85rem' }}
                                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M11.5 15a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm-3 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm-3 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Zm-3 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1Z" />
                                    <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8ZM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4Z" />
                                    <path d="M5 3h6v1H5V3Zm0 2h6v1H5V5Zm0 2h6v1H5V7Zm0 2h3v1H5V9Z" />
                                </svg>
                                {showSelectedOnly ? 'Showing Assigned' : 'Show Assigned Only'}
                            </button>
                        </div>
                    </div>
                </div>


                <div className="accordion border-0">
                    {
                        console.log("Rendering AccessPermission with data:", filteredData)
                    }
                    {filteredData?.length > 0 ? (
                        filteredData?.map((module) => {
                            const isOpen = openModuleId === module?.CategoryName;
                            // const moduleAllIds = module.subModules.flatMap(s => s.permissions.map(p => p.id));
                            // const moduleSelectedCount = moduleAllIds.filter(id => selectedPermissions.has(id)).length;
                            // const isModuleFull = moduleSelectedCount === moduleAllIds.length && moduleAllIds.length > 0;
                            // const isModuleIndeterminate = moduleSelectedCount > 0 && moduleSelectedCount < moduleAllIds.length;
                            let allSelcted = areAllSelected(module.Permissions);

                            return (
                                <div
                                    className="accordion-item mb-3 border-0 shadow-sm rounded overflow-hidden "
                                    key={module.CategoryName}
                                    ref={el => moduleRefs.current[module.CategoryName] = el}
                                >
                                    <h2 className="accordion-header">
                                        <div className={`pl-4 d-flex align-items-center bg-white pe-3 border-bottom transition-all ${isOpen ? 'border-primary' : ''}`}>
                                            <button
                                                className={`accordion-button shadow-none fw-bold py-3 text-dark ${isOpen ? '' : 'collapsed'}`}
                                                type="button"
                                                onClick={() => toggleAccordion(module.CategoryName)}
                                            >
                                                <span className="text-truncate">{module?.CategoryName}</span>
                                                {/* {moduleSelectedCount > 0 && (
                                                    <span className="badge bg-primary rounded-pill ms-2" style={{ fontSize: '0.65rem' }}>
                                                        {moduleSelectedCount} Selected
                                                    </span>
                                                )} */}
                                            </button>

                                            <div className="d-flex align-items-center ms-auto ps-2">
                                                <label
                                                    className="x-small text-muted me-2 fw-bold mb-0"
                                                    style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                                                    htmlFor={`all-${module.CategoryName}`}
                                                >
                                                    ALL
                                                </label>
                                                <div className="form-check mb-0">
                                                    <input
                                                        className="form-check-input mt-0 indeterminate-checkbox"
                                                        type="checkbox"
                                                        id={`all-${module.CategoryName}`}
                                                        checked={allSelcted}
                                                        // ref={el => { if (el) el.indeterminate = isModuleIndeterminate; }}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleUpdateAllPer(module.CategoryName, e.target.checked);
                                                            // handleBatchToggle(moduleAllIds, !isModuleFull);
                                                        }}
                                                        style={{ width: '1em', height: '1em', cursor: 'pointer' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </h2>

                                    <div className={`accordion-collapse ${isOpen ? 'show' : 'd-none'}`}>
                                        <div className="accordion-body bg-light pt-3 px-3 pb-2">
                                            {module.Permissions.map(sub => {

                                                let allSelc = areAllSelected(sub.SubPermissions || []);

                                                return <SubModuleSection
                                                    key={sub.id}
                                                    subModule={sub}
                                                    selectedIds={selectedPermissions}
                                                    onBatchChange={(perm, isChecked) => updateModuleSubmodule(perm, isChecked)}
                                                    onSingleChange={(id, isSelected) => handleSinglePermissionChange(id, isSelected)}
                                                    areAllSelected={allSelc}
                                                />
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-5 bg-white rounded shadow-sm border">
                            <p className="text-muted mb-0">
                                {showSelectedOnly
                                    ? "No assigned permissions found. Uncheck 'Showing Assigned' or select some permissions first."
                                    : "No modules match your search."}
                            </p>
                            {showSelectedOnly && (
                                <button className="btn btn-link btn-sm mt-2" onClick={() => setShowSelectedOnly(false)}>
                                    Show All Modules
                                </button>
                            )}
                        </div>
                    )}
                </div>


                <div className="sticky-bottom mt-5 p-3 bg-white border rounded shadow-lg d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div className="d-flex align-items-center">
                        <div className="bg-dark text-white rounded px-3 py-1 me-3 small">
                            {
                                console.log(permissionData, 'AMir')
                            }
                            <strong>{selectedPermission?.length}</strong> Permissions Selected
                        </div>
                        {/* <button className="btn btn-link text-danger btn-sm text-decoration-none fw-bold p-0" onClick={() => setSelectedPermissions(new Set())}>
                            RESET ALL
                        </button> */}
                    </div>
                    <button className="btn btn-primary px-5 py-2 fw-bold shadow-sm" onClick={updatePermissions}>
                        SAVE PERMISSIONS
                    </button>
                </div>
            </div>


            <style>{`
        .accordion-button:not(.collapsed) { background-color: #fcfcfc; color: #0d6efd !important; }
        .hover-shadow:hover { box-shadow: 0 4px 8px rgba(0,0,0,0.08) !important; border-color: #0d6efd !important; }
        .x-small { font-size: 0.7rem; letter-spacing: 0.5px; }
        .form-check-input.indeterminate-switch:indeterminate {
          background-color: #adb5bd !important; 
          border-color: #adb5bd !important;
        //   background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3e%3c/svg%3e") !important;
        }
        .form-check-input.indeterminate-checkbox:indeterminate { background-color: #6c757d; border-color: #6c757d; }
        .form-check-input:checked { background-color: #0d6efd; border-color: #0d6efd; }
        .sticky-bottom { position: sticky; bottom: 20px; z-index: 1020; }
        .transition-all { transition: all 0.2s ease-in-out; }
        
        .btn-outline-primary:hover {
          background-color: #f0f7ff;
          color: #0d6efd;
        }
      `}</style>
        </div>
    );
}



